"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { useEffect, useMemo, useRef } from "react";
import { googleAnalyticsClientEnv } from "../lib/analytics/google-analytics.client.env.ts";
type GoogleTagFunction = (command: "js" | "config" | "event", value: string, params?: Record<string, string | number | boolean | undefined>) => void;
declare global {
  var gtag: GoogleTagFunction | undefined;
}
const isGoogleTagFunction = (value: unknown): value is GoogleTagFunction => typeof value === "function";
const getGlobalGtag = (): GoogleTagFunction | undefined => {
  const candidate: unknown = globalThis.gtag;
  if (!isGoogleTagFunction(candidate)) {
    return undefined;
  }
  return candidate;
};
const isAllowedForCurrentLocation = (allowedHosts: string[], allowedOrigins: string[]): boolean => {
  if (globalThis.location === undefined) {
    return true;
  }
  const hostname = globalThis.location.hostname.toLowerCase();
  const origin = globalThis.location.origin.toLowerCase();
  const hostAllowed = allowedHosts.length === 0 || allowedHosts.includes(hostname);
  const originAllowed = allowedOrigins.length === 0 || allowedOrigins.includes(origin);
  return hostAllowed && originAllowed;
};
const sendPageView = (input: {
  pathname: string | null;
}) => {
  const gtag = getGlobalGtag();
  if (typeof gtag !== "function") {
    return;
  }
  gtag("event", "page_view", {
    page_location: globalThis.location.href,
    page_path: input.pathname ?? "/",
    page_title: globalThis.document.title
  });
};
export const GoogleAnalytics = () => {
  const $ = _c(6);
  const pathname = usePathname();
  const hasMountedRef = useRef(false);
  const measurementId = googleAnalyticsClientEnv.measurementId;
  const {
    allowedHosts,
    allowedOrigins
  } = googleAnalyticsClientEnv;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = isAllowedForCurrentLocation(allowedHosts, allowedOrigins);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const isAllowed = t0;
  let t1;
  let t2;
  if ($[1] !== pathname) {
    t1 = () => {
      if (!measurementId) {
        return;
      }
      if (!isAllowed) {
        return;
      }
      if (!hasMountedRef.current) {
        hasMountedRef.current = true;
        return;
      }
      sendPageView({
        pathname
      });
    };
    t2 = [isAllowed, measurementId, pathname];
    $[1] = pathname;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (!measurementId || !isAllowed) {
    return null;
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = JSON.stringify({
      send_page_view: false
    });
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const gtagConfig = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <><Script id="google-analytics-src" strategy="afterInteractive" src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`} /><Script id="google-analytics-init" strategy="afterInteractive">{`window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        gtag('js', new Date());
        gtag('config', '${measurementId}', ${gtagConfig});`}</Script></>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};